import React, { Fragment, useState, useEffect } from 'react';
import { Edit, Trash, ChevronDown, ChevronUp, X, AlertTriangle, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  useListRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useListPermissions,
} from '@/hooks/useIAM';
import { Role, CreateRoleData, UpdateRoleData, Permission } from '@/types/iam';
import { cn } from '@/lib/utils';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  roleName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  roleName: string;
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    const toastId = toast.loading(`Deleting role "${roleName}"...`);

    try {
      await onConfirm();

      toast.success(`Role "${roleName}" deleted successfully`, { id: toastId });

      onClose();
    } catch (error) {
      console.error('Error deleting role:', error);

      toast.error(`Error deleting role "${roleName}". Please try again.`, {
        id: toastId,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
    >
      <div
        className="rounded-lg shadow-lg max-w-md w-full bg-background"
      >
        <div className="p-5 border-b border-border flex justify-between items-center">
          <div className="flex items-center text-secondary">
            <AlertTriangle className="mr-2" size={20} />
            <h3 className="text-lg font-medium">Confirm Delete</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity text-muted-foreground"
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <p className="mb-4 text-foreground">
            Are you sure you want to delete the role{' '}
            <span className="font-semibold">{roleName}</span>? This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-md transition-colors flex items-center bg-destructive text-destructive-foreground hover:opacity-90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoleForm = ({
  role,
  permissions,
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  role?: Role;
  permissions: Permission[];
  isOpen: boolean;
  onSubmit: (data: CreateRoleData | UpdateRoleData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}) => {
  const [formData, setFormData] = useState({
    id: role?.id || '',
    name: role?.name || '',
    description: role?.description || '',
    permissions: role?.permissions || [],
    priority: role?.priority || 0,
  });

  useEffect(() => {
    setFormData({
      id: role?.id || '',
      name: role?.name || '',
      description: role?.description || '',
      permissions: role?.permissions || [],
      priority: role?.priority || 0,
    });
  }, [role]);

  const handlePermissionToggle = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      ...(role && { id: role.id }),
    };

    await onSubmit(submitData);
  };

  return (
    isOpen && (
      <div
        className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
      >
        <div className="rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-background">
          <div className="p-5 border-b border-border flex justify-between items-center">
            <h3 className="text-lg font-medium text-foreground">
              {role ? 'Edit Role' : 'Create Role'}
            </h3>
            <button
              onClick={onCancel}
             className="hover:opacity-70 transition-opacity text-muted-foreground"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Id *
              </label>
              <input
                type="text"
                required
                disabled={!!role}
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Priority
              </label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3 text-foreground">
                Permissions
              </label>
              <div className="max-h-60 overflow-y-auto border border-muted-foreground rounded-md p-3">
                {permissions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No permissions available. Create permissions first.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {permissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-center space-x-3 cursor-pointer hover:opacity-80"
                      >
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="w-4 h-4 rounded accent-primary"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {permission.name}
                          </div>
                          {permission.description && (
                            <div className="text-xs text-muted-foreground">
                              {permission.description}
                            </div>
                          )}
                          {permission.resource && (
                            <div className="text-xs text-primary">
                              Resource: {permission.resource}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Selected: {formData.permissions.length} permission(s)
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {role ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  role ? 'Update' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

const RolesTable = () => {
  const { roles, fetchRoles, loading } = useListRoles();
  const { createRole, loading: createLoading } = useCreateRole();
  const { updateRole, loading: updateLoading } = useUpdateRole();
  const { deleteRole } = useDeleteRole();
  const { permissions, fetchPermissions } = useListPermissions();

  const [sortField, setSortField] = useState<keyof Role>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Initialize data
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  const showDeleteConfirmation = (id: string) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  const handleDeleteRole = async () => {
    if (deleteId) {
      await deleteRole(deleteId);
      await fetchRoles();
      setIsModalOpen(false);
      setDeleteId(null);
    }
  };

  const handleSort = (field: keyof Role) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRoles = [...roles].sort((a, b) => {
    const aValue = (a[sortField] as string)?.toLowerCase() || '';
    const bValue = (b[sortField] as string)?.toLowerCase() || '';

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const renderSortIndicator = (field: keyof Role) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="ml-1" />
    ) : (
      <ChevronDown size={16} className="ml-1" />
    );
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setIsFormOpen(true);
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateRoleData | UpdateRoleData) => {
    try {
      if (selectedRole) {
        await updateRole(data as UpdateRoleData);
      } else {
        await createRole(data as CreateRoleData);
      }
      await fetchRoles();
      setIsFormOpen(false);
      setSelectedRole(null);
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  const roleToDelete = deleteId
    ? roles.find((role) => role.id === deleteId)?.name || 'this role'
    : '';


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with title and button to add */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-foreground">
          Roles
        </h2>

        <button
          onClick={handleCreateRole}
          className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
        >
          <Plus size={16} className="mr-2" />
          Add Role
        </button>
      </div>

      {/* Table of roles */}
      <div className="overflow-x-auto rounded-lg shadow bg-background">
        <table className="min-w-full divide-y bg-background">
          <thead className="">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-primary"
              >
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Name {renderSortIndicator('name')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('description')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Description {renderSortIndicator('description')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-primary"
              >
                Permissions
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-primary"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border w-full">
            {sortedRoles.length === 0 ? (
              <tr className="w-full">
                <td colSpan={4} className="px-6 py-8 text-center w-full">
                  No roles have been added yet. Use the "Add Role" button to create one.
                </td>
              </tr>
            ) : (
              sortedRoles.map((role) => (
                <Fragment key={role.id}>
                  <tr className="hover:bg-muted/50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">
                            {role.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm truncate max-w-xs text-muted-foreground">
                        {role.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.length === 0 ? (
                          <span
                            className="px-2 py-1 text-xs rounded-full bg-muted/60 text-muted-foreground font-medium"
                          >
                            No permissions
                          </span>
                        ) : (
                          role.permissions.slice(0, 3).map((permissionId) => {
                            const permission = permissions.find(p => p.id === permissionId);
                            return permission ? (
                                <span
                                  key={permissionId}
                                  className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium"
                                >
                                {permission.name}
                              </span>
                            ) : null;
                          })
                        )}
                        {role.permissions.length > 3 && (
                          <span
                            className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium"
                          >
                            +{role.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditRole(role)}
                        className="ml-4 transition-colors text-primary hover:opacity-70"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(role.id)}
                        className="ml-4 transition-colors text-destructive hover:opacity-70"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <RoleForm
        role={selectedRole || undefined}
        permissions={permissions}
        isOpen={isFormOpen}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setSelectedRole(null);
        }}
        isSubmitting={createLoading || updateLoading}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteRole}
        roleName={roleToDelete}
      />
    </div>
  );
};

export default RolesTable;
